import { array, boolean, mixed, object, string } from 'yup';

import {
	validateDecimalNumber,
	validateFloatingPointLength,
	validateLatitude,
	validateLengthPrice,
	validateLongitude,
	validateUtmCoordinateValue,
} from '@utils/validation';

import { ECoordFormat, TCoordFormat } from '@enums/common';
import { EAssetType as AssetTypeEnum } from '@enums/map';

import { getMaxLengthErrMsg, mandatoryErrMessage, validationErrMessages } from '@constants/validationConstants';
import {
	MAX_3_LENGTH,
	MAX_10_LENGTH,
	MAX_12_LENGTH,
	MAX_50_LENGTH,
	MAX_500_LENGTH,
	MAX_1000_LENGTH,
} from '@constants/validationMessages';
import { excludeFieldsForEditMode, formRequiredFieldNames } from '@components/ModalComponents/AssetForm/constants';

export const mainSchema = ({
	requiredFields,
	isEdit,
}: {
	requiredFields: string[] | null;
	isEdit: boolean;
}) => {
	const schema = object({
		assetId: string()
			.trim()
			.default('')
			.required(validationErrMessages.assetId)
			.max(MAX_50_LENGTH, getMaxLengthErrMsg('Asset ID', MAX_50_LENGTH)),
		contactPersonId: object()
			.nullable(),
		assetType: object()
			.nullable()
			.required(validationErrMessages.assetType),
		subType: object()
			.nullable(),
		uprn: string()
			.trim()
			.default('')
			.max(MAX_12_LENGTH, getMaxLengthErrMsg('UPRN', MAX_12_LENGTH)),
		responsible: object()
			.nullable(),
		indicativeAcceptablePricing: string()
			.trim()
			.required(validationErrMessages.price)
			.test('validateDecimalNumber', validationErrMessages.priceValid, validateDecimalNumber)
			.test('validateLengthPrice', getMaxLengthErrMsg('Price', MAX_10_LENGTH), validateLengthPrice)
			.test('validateFloatingPoint', 'The floating value must be less than 2 digits', (value) => validateFloatingPointLength(value as string, 2)),
		maxOperators: string()
			.trim()
			.max(MAX_3_LENGTH, getMaxLengthErrMsg('Operators', MAX_3_LENGTH)),
		height: string()
			.trim()
			.max(MAX_3_LENGTH, getMaxLengthErrMsg('Height', MAX_3_LENGTH)),
		assetOwnerNotes: string()
			.trim()
			.max(MAX_500_LENGTH, getMaxLengthErrMsg('Asset Notes', MAX_500_LENGTH)),
		lat: mixed().when('coordFormat', {
			is: (coordFormat: TCoordFormat) => coordFormat === ECoordFormat.latlon,
			then: string()
				.trim()
				.test('validateLatlonCoords', validationErrMessages.latValid, validateLatitude)
				.required(validationErrMessages.lat),
			otherwise: string()
				.trim()
				.required(validationErrMessages.easting)
				.max(MAX_10_LENGTH, validationErrMessages.eastingValidLen)
				.test('validateCoordsValue', validationErrMessages.eastingValid, validateUtmCoordinateValue),
		}),
		lng: mixed().when('coordFormat', {
			is: (coordFormat: TCoordFormat) => coordFormat === ECoordFormat.latlon,
			then: string()
				.trim()
				.test('validateLatlonCoords', validationErrMessages.lngValid, validateLongitude)
				.required(validationErrMessages.lng),
			otherwise: string()
				.trim()
				.required(validationErrMessages.northing)
				.max(MAX_10_LENGTH, validationErrMessages.northingValidLen)
				.test('validateCoordsValue', validationErrMessages.northingValid, validateUtmCoordinateValue),
		}),
		locationDescription: string()
			.trim()
			.max(MAX_500_LENGTH, getMaxLengthErrMsg('Location Description', MAX_500_LENGTH)),
		adPropertyNoName: mixed().when('assetType', {
			is: (assetType: any) => assetType?.value === AssetTypeEnum.building
				|| assetType?.value === AssetTypeEnum.land,
			then: string()
				.trim()
				.default('')
				.required(validationErrMessages.adPropertyNoName)
				.max(MAX_50_LENGTH, getMaxLengthErrMsg('Property No./Name', MAX_50_LENGTH)),
			otherwise: string().nullable().notRequired(),
		}),
		adStreetName: mixed().when('assetType', {
			is: (assetType: any) => assetType?.value === AssetTypeEnum.building
				|| assetType?.value === AssetTypeEnum.land,
			then: string()
				.trim()
				.default('')
				.required(validationErrMessages.adStreetName)
				.max(MAX_50_LENGTH, getMaxLengthErrMsg('Street Name', MAX_50_LENGTH)),
			otherwise: string().nullable().notRequired(),
		}),
		adLocality:
			string()
				.trim()
				.default('')
				.nullable()
				.max(MAX_50_LENGTH, getMaxLengthErrMsg('Locality', MAX_50_LENGTH)),
		adTownCity: mixed().when('assetType', {
			is: (assetType: any) => assetType?.value === AssetTypeEnum.building
				|| assetType?.value === AssetTypeEnum.land,
			then: string()
				.trim()
				.default('')
				.required(validationErrMessages.adTownCity)
				.max(MAX_50_LENGTH, getMaxLengthErrMsg('Town/City', MAX_50_LENGTH)),
			otherwise: string().nullable().notRequired(),
		}),
		ngr: string()
			.trim()
			.max(MAX_12_LENGTH, getMaxLengthErrMsg('NGR', MAX_12_LENGTH)),
		adPostCode: mixed().when('assetType', {
			is: (assetType: any) => assetType?.value === AssetTypeEnum.building
				|| assetType?.value === AssetTypeEnum.land,
			then: string()
				.trim()
				.default('')
				.required(validationErrMessages.adPostCode)
				.max(MAX_10_LENGTH, getMaxLengthErrMsg('Postcode', MAX_10_LENGTH)),
			otherwise: string().nullable().notRequired(),
		}),
		backhaulAccessibilityStatus: string()
			.required(),
		powerSupplyAvailabilityStatus: string()
			.required(),
		assetSiteAvailabilityStatus: string()
			.required(),
		specialImplementationIssuesExist: boolean()
			.required(),
		isAvailable: boolean()
			.required(),
		lastVerificationDate: string()
			.nullable(),
		siteAvailabilityReviewDate: string()
			.nullable(),
		backhaulAccessibilityDetails: string()
			.trim()
			.default('')
			.max(MAX_1000_LENGTH, getMaxLengthErrMsg('Details', MAX_1000_LENGTH)),
		powerSupplyAvailabilityDetails: string()
			.trim()
			.default('')
			.max(MAX_1000_LENGTH, getMaxLengthErrMsg('Details', MAX_1000_LENGTH)),
		assetSiteAvailabilityDetails: string()
			.trim()
			.default('')
			.max(MAX_1000_LENGTH, getMaxLengthErrMsg('Details', MAX_1000_LENGTH)),
		specialImplementationIssueDetails: string()
			.trim()
			.default('')
			.max(MAX_1000_LENGTH, getMaxLengthErrMsg('Details', MAX_1000_LENGTH)),
		assetAccessDetails: string()
			.trim()
			.default('')
			.max(MAX_1000_LENGTH, getMaxLengthErrMsg('Details', MAX_1000_LENGTH)),
		assetAccessDetailsFileIds: array()
			.default([]),
		backhaulAccessibilityDetailsFileIds: array()
			.default([]),
		assetSiteAvailabilityDetailsFileIds: array()
			.default([]),
		powerSupplyAvailabilityDetailsFileIds: array()
			.default([]),
		specialImplementationIssueDetailsFileIds: array()
			.default([]),
	});

	const anySchema = schema as any;

	if (requiredFields?.length) {
		requiredFields
			.filter(field => !(isEdit && excludeFieldsForEditMode.includes(field)))
			.forEach((fieldName) => {
				if (Object.hasOwn(anySchema.fields, fieldName)) {
					anySchema
						.fields[fieldName] = anySchema
						.fields[fieldName]
						.required(mandatoryErrMessage(
							Object.hasOwn(formRequiredFieldNames, fieldName)
								? formRequiredFieldNames[fieldName]
								: fieldName));
				}
			});
	}

	return schema;
};
